<template>
  <InfuseModal :title="t('e_connector.chargingStations.modal.update.title')" @close="emit('close')">
    <form ref="form" id="update-charging-station" @submit.prevent="onSave()">
      <div class="flex max-h-[475px] max-w-[675px] flex-col gap-4 overflow-y-auto px-5">
        <div class="flex items-start gap-4">
          <InfuseField :label="t('e_connector.common.chargingStation.name')" class="basis-full" size="small">
            <InfuseInput
              size="small"
              v-model="chargingStationData.name"
              :maxlength="255"
              :placeholder="t('e_connector.chargingStations.modal.field.name.placeholder')"
              :required="true"
            />
          </InfuseField>
        </div>

        <div class="flex items-start gap-4">
          <InfuseField :label="t('e_connector.common.chargingStation.ocppVersion')" class="basis-1/2" size="small">
            <InfuseSelect
              name="ocppVersion"
              size="small"
              v-model="chargingStationData.ocppVersion"
              :options="ocppVersionOptions"
              :label="t('e_connector.chargingStations.modal.field.ocppVersion.placeholder')"
              :required="true"
            />
          </InfuseField>

          <InfuseField :label="t('e_connector.common.chargingStation.type')" class="basis-1/2" size="small">
            <InfuseSelect
              name="type"
              size="small"
              v-model="chargingStationData.type"
              :options="typeOptions"
              :label="t('e_connector.chargingStations.modal.field.type.placeholder')"
              :required="true"
            />
          </InfuseField>
        </div>

        <div v-if="chargingStationData.type === ConnectionType.SOAP" class="flex items-start gap-4">
          <InfuseField :label="t('e_connector.common.chargingStation.url')" optional class="basis-1/2" size="small">
            <InfuseInput
              size="small"
              v-model="chargingStationData.uri"
              type="url"
              :placeholder="t('e_connector.chargingStations.modal.field.url.placeholder')"
              :disabled="chargingStationData.useRequestHeader"
            />
          </InfuseField>

          <InfuseField :label="t('e_connector.common.chargingStation.useRequestHeader')" class="basis-1/4" size="small">
            <InfuseToggle
              id="useRequestHeader"
              :value="chargingStationData.useRequestHeader"
              :disabled="!!chargingStationData.uri"
              @on="chargingStationData.useRequestHeader = true"
              @off="chargingStationData.useRequestHeader = false"
            />
          </InfuseField>

          <InfuseField :label="t('e_connector.common.chargingStation.omitSoapHeader')" class="basis-1/4" size="small">
            <InfuseToggle
              id="omitSoapHeader"
              :value="chargingStationData.omitSoapHeader"
              @on="chargingStationData.omitSoapHeader = true"
              @off="chargingStationData.omitSoapHeader = false"
            />
          </InfuseField>
        </div>

        <div class="flex items-start gap-4">
          <InfuseField
            :label="t('e_connector.common.chargingStation.networkProfile')"
            :helpText="t('e_connector.chargingStations.modal.field.networkProfile.help')"
            class="basis-full"
            size="small"
          >
            <InfuseSelect
              name="networkProfile"
              size="small"
              v-model="chargingStationData.networkProfileId"
              :options="networkProfileOptions"
              :label="t('e_connector.chargingStations.modal.field.networkProfile.placeholder')"
              :required="true"
            />
          </InfuseField>
        </div>

        <h1>Basic auth</h1>

        <div class="flex items-start gap-4">
          <InfuseField
            :label="t('e_connector.common.chargingStation.basicAuthStatus')"
            :helpText="basicAuthStatusHelpText"
            class="basis-1/2"
            size="small"
          >
            <InfuseSelect
              name="basicAuthStatus"
              size="small"
              v-model="chargingStationData.basicAuthStatus"
              :options="basicAuthStatusOptions"
              :label="t('e_connector.chargingStations.modal.field.basicAuthStatus.placeholder')"
              :required="true"
            />
          </InfuseField>

          <InfuseField
            :label="t('e_connector.chargingStations.modal.field.basicAuthDeviation.label')"
            :helpText="t('e_connector.chargingStations.modal.field.basicAuthDeviation.help')"
            class="basis-1/2"
            size="small"
          >
            <InfuseSelect
              name="type"
              size="small"
              v-model="basicAuthDeviationId"
              :options="basicAuthDeviationOptions"
              :label="t('e_connector.chargingStations.modal.field.basicAuthDeviation.placeholder')"
              :required="true"
            />
          </InfuseField>
        </div>

        <div class="flex items-start gap-4">
          <InfuseField
            :label="t('e_connector.chargingStations.modal.field.shouldSetBasicAuthPassword.label')"
            class="basis-1/2"
            size="small"
          >
            <InfuseToggle
              id="shouldSetBasicAuthPassword"
              :value="shouldSetNewBasicAuthPassword"
              @on="shouldSetNewBasicAuthPassword = true"
              @off="shouldSetNewBasicAuthPassword = false"
            />
          </InfuseField>

          <InfuseField
            :label="t('e_connector.common.chargingStation.basicAuthPassword')"
            class="basis-1/2"
            :class="{ invisible: !shouldSetNewBasicAuthPassword }"
            size="small"
          >
            <template #label>
              <InfuseTooltip
                :content="t('e_connector.chargingStations.modal.field.shouldSetBasicAuthPassword.tooltip')"
                class="inline cursor-help text-yellow-500"
              >
                <font-awesome-icon :icon="['fal', 'circle-exclamation']" />
              </InfuseTooltip>
            </template>

            <InfuseInput
              size="small"
              class="relative"
              v-model="chargingStationData.basicAuthPassword"
              :maxlength="255"
              :placeholder="t('e_connector.chargingStations.modal.field.basicAuthPassword.placeholder')"
              :required="false"
            >
              <template #clear="{ value }">
                <InfuseCopy v-slot="scope" class="absolute">
                  <font-awesome-icon
                    class="w-5/6 cursor-pointer text-neutral-500 hover:text-neutral-600"
                    :icon="['far', 'copy']"
                    @click="scope.copy(value.toString())"
                  />
                </InfuseCopy>
              </template>
            </InfuseInput>
          </InfuseField>
        </div>
      </div>
    </form>

    <template #actions>
      <InfuseButton color="secondary" @click="emit('close')">
        {{ t('e_connector.common.action.cancel') }}
      </InfuseButton>
      <InfuseButton type="submit" form="update-charging-station" :disabled="loading" :loading="loading">
        {{ t('e_connector.common.action.update') }}
      </InfuseButton>
    </template>
  </InfuseModal>
</template>

<script setup lang="ts">
import {
  InfuseButton,
  InfuseCopy,
  InfuseField,
  InfuseInput,
  InfuseModal,
  InfuseSelect,
  InfuseToggle,
  InfuseTooltip
} from '@infuse/ui/components';
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { ConnectionType, OcppVersion } from '@infuse/e-connector/central-system/domain';
import { BasicAuthStatus, ChargingStation } from '@infuse/e-connector/charging-station/domain';
import { NetworkProfile } from '@infuse/e-connector/network-profile/domain';
import { BasicAuthDeviation } from '@infuse/e-connector/basic-auth-deviation/domain';
import { generateBasicAuthPassword } from '@infuse/e-connector/charging-station/utils';

interface Props {
  loading: boolean;
  chargingStation: ChargingStation;
  networkProfiles: NetworkProfile[];
  basicAuthDeviations: BasicAuthDeviation[];
}

interface Emits {
  (e: 'close'): void;
  (e: 'update', chargingStation: ChargingStation): void;
}

const props = withDefaults(defineProps<Props>(), {
  loading: false
});

const emit = defineEmits<Emits>();

const { t } = useI18n();

const chargingStationData = ref<ChargingStation>({ ...props.chargingStation }); // copy
const shouldSetNewBasicAuthPassword = ref<boolean>(!!chargingStationData.value.basicAuthPassword);
const basicAuthDeviationId = ref<number>(
  !chargingStationData.value.basicAuthDeviationId ? -1 : +chargingStationData.value.basicAuthDeviationId
); // default to -1 if no deviation is selected

const typeOptions = Object.values(ConnectionType);
const ocppVersionOptions = Object.values(OcppVersion);
const networkProfileOptions = computed(() =>
  props.networkProfiles.map((networkProfile) => ({
    key: networkProfile.name,
    value: networkProfile.id
  }))
);
const basicAuthDeviationOptions = computed(() => [
  {
    key: t('e_connector.chargingStations.modal.field.basicAuthDeviation.default'),
    value: -1
  },
  ...props.basicAuthDeviations.map((d) => ({
    key: d.label,
    value: d.id
  }))
]);
const basicAuthStatusOptions = Object.values(BasicAuthStatus);

const basicAuthStatusHelpText = computed(() => {
  switch (chargingStationData.value.basicAuthStatus) {
    case BasicAuthStatus.REQUIRED:
      return t('e_connector.chargingStations.modal.field.basicAuthStatus.help.required');
    case BasicAuthStatus.NOT_REQUIRED:
      return t('e_connector.chargingStations.modal.field.basicAuthStatus.help.notRequired');
    case BasicAuthStatus.ENABLE_NEXT_CONNECT:
      return t('e_connector.chargingStations.modal.field.basicAuthStatus.help.enableNextConnect');
    case BasicAuthStatus.CONFIGURE_NEXT_CONNECT:
      return t('e_connector.chargingStations.modal.field.basicAuthStatus.help.configureNextConnect');
    default:
      return '';
  }
});

watch(
  () => shouldSetNewBasicAuthPassword.value,
  (newValue) => {
    chargingStationData.value.basicAuthPassword = newValue ? generateBasicAuthPassword() : '';
  }
);

watch(
  () => chargingStationData.value.type,
  (newValue) => {
    if (newValue !== ConnectionType.SOAP) {
      chargingStationData.value.uri = '';
      chargingStationData.value.useRequestHeader = false;
      chargingStationData.value.omitSoapHeader = false;
    }
  }
);

function onSave() {
  if (!shouldSetNewBasicAuthPassword.value) {
    chargingStationData.value.basicAuthPassword = undefined;
  }

  chargingStationData.value.basicAuthDeviationId = basicAuthDeviationId.value.toString();

  emit('update', chargingStationData.value);
}
</script>
